// src/components/Projects/ProjectsListView.tsx
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, FolderKanban, Archive, Users, MoreHorizontal, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { usePermissions } from '@/hooks/usePermissions';
import { useProjects } from '@/hooks/useProjects';
import type { Project } from '@/types/project';
import CreateProjectDialog from './CreateProjectDialog';

const ROLE_LABELS: Record<string, string> = {
  owner: 'Responsável',
  editor: 'Editor',
  viewer: 'Visualizador',
};

const ROLE_VARIANTS: Record<string, 'default' | 'secondary' | 'outline'> = {
  owner: 'default',
  editor: 'secondary',
  viewer: 'outline',
};

function ProjectCard({
  project,
  onClick,
  onArchive,
  onDelete,
  isOwner,
}: {
  project: Project;
  onClick: () => void;
  onArchive: () => void;
  onDelete: () => void;
  isOwner: boolean;
}) {
  return (
    <div
      className="group relative bg-card border border-border rounded-lg p-4 cursor-pointer hover:border-primary/50 hover:shadow-sm transition-all"
      onClick={onClick}
    >
      {/* Barra de cor lateral */}
      <div
        className="absolute left-0 top-3 bottom-3 w-1 rounded-r-full"
        style={{ backgroundColor: project.color ?? '#6366F1' }}
      />

      {/* Menu de contexto ⋮ */}
      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem
              onClick={(e) => { e.stopPropagation(); onArchive(); }}
            >
              <Archive className="h-4 w-4 mr-2" />
              Arquivar
            </DropdownMenuItem>
            {isOwner && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={(e) => { e.stopPropagation(); onDelete(); }}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Excluir projeto
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="pl-3">
        {/* Cabeçalho */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="font-medium text-sm leading-snug line-clamp-2 group-hover:text-primary transition-colors">
            {project.name}
          </h3>
          {project.my_role && (
            <Badge
              variant={ROLE_VARIANTS[project.my_role] ?? 'outline'}
              className="text-xs shrink-0"
            >
              {ROLE_LABELS[project.my_role] ?? project.my_role}
            </Badge>
          )}
        </div>

        {/* Descrição */}
        {project.description && (
          <p className="text-xs text-muted-foreground line-clamp-2 mb-3">
            {project.description}
          </p>
        )}

        {/* Rodapé */}
        <div className="flex items-center gap-3 text-xs text-muted-foreground mt-auto">
          <span className="flex items-center gap-1">
            <Users className="h-3 w-3" />
            {project.member_count ?? 0}{' '}
            {(project.member_count ?? 0) === 1 ? 'membro' : 'membros'}
          </span>
        </div>
      </div>
    </div>
  );
}

export default function ProjectsListView() {
  const { canAccess } = usePermissions();
  const navigate = useNavigate();

  const [showArchived, setShowArchived] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [deletingProjectId, setDeletingProjectId] = useState<string | null>(null);

  const {
    projects,
    isLoading,
    archiveProject,
    deleteProject,
    isDeletingProject,
  } = useProjects(showArchived);

  if (!canAccess('projects:view')) {
    return (
      <div className="p-6 text-muted-foreground text-sm">
        Você não tem permissão para acessar projetos.
      </div>
    );
  }

  const visibleProjects = projects;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Projetos</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Projetos compartilhados da equipe
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowArchived((v) => !v)}
            className="text-muted-foreground"
          >
            <Archive className="h-4 w-4 mr-1.5" />
            {showArchived ? 'Ver ativos' : 'Ver arquivados'}
          </Button>

          {canAccess('projects:create') && (
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4 mr-1.5" />
              Novo projeto
            </Button>
          )}
        </div>
      </div>

      {/* Grid de projetos */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="h-32 rounded-lg bg-muted animate-pulse"
            />
          ))}
        </div>
      ) : visibleProjects.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <FolderKanban className="h-12 w-12 text-muted-foreground/30 mb-4" />
          {showArchived ? (
            <>
              <p className="text-sm font-medium">Nenhum projeto arquivado</p>
              <p className="text-xs text-muted-foreground mt-1">
                Projetos arquivados aparecerão aqui.
              </p>
            </>
          ) : (
            <>
              <p className="text-sm font-medium">Nenhum projeto ainda</p>
              <p className="text-xs text-muted-foreground mt-1 mb-4">
                Crie o primeiro projeto da equipe.
              </p>
              {canAccess('projects:create') && (
                <Button size="sm" onClick={() => setCreateOpen(true)}>
                  <Plus className="h-4 w-4 mr-1.5" />
                  Criar projeto
                </Button>
              )}
            </>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {visibleProjects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              onClick={() => navigate(`/dashboard/projects/${project.id}`)}
              onArchive={() => archiveProject(project.id)}
              onDelete={() => setDeletingProjectId(project.id)}
              isOwner={project.my_role === 'owner'}
            />
          ))}
        </div>
      )}

      {/* Dialog de criação */}
      <CreateProjectDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
      />

      {/* AlertDialog de confirmação de exclusão */}
      <AlertDialog
        open={!!deletingProjectId}
        onOpenChange={(open) => { if (!open) setDeletingProjectId(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir projeto?</AlertDialogTitle>
            <AlertDialogDescription>
              O projeto será excluído permanentemente. As tarefas vinculadas
              não serão excluídas — perderão apenas a referência ao projeto.
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deletingProjectId) {
                  deleteProject(deletingProjectId);
                  setDeletingProjectId(null);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isDeletingProject}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
